import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {ComponentPortal} from "@angular/cdk/portal";
import {AsyncPipe} from '@angular/common';
import {Component, Injector, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatFormField} from '@angular/material/form-field';
import {MatInput, MatLabel} from '@angular/material/input';

import {AnnotationListItemComponent} from "@app/annotation/annotation-list-item/annotation-list-item.component";
import {Annotation, AnnotationService} from '@app/annotation/annotation.service';
import {MENTION_QUERY, MentionOverlayComponent} from "@app/annotation/mention-overlay/mention-overlay.component";
import {Asset, AssetService, AssetType, deserializeAsset, VideoAsset} from '@app/asset';
import {Slot} from '@app/review/models';
import {PlaybackState, ReviewVideoService, Source} from '@app/review/review-video.service';
import {ReviewService} from '@app/review/review.service';
import {MessageType} from '@app/websocket/websocket.service';
import {User} from "@shared/user.service";

import {Observable} from 'rxjs';
import {filter} from 'rxjs/operators';


@Component({
    selector: 'frog-review-annotation-list',
    imports: [
        MatButton,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        MatCheckbox,
        AsyncPipe,
        AnnotationListItemComponent,
    ],
    templateUrl: './review-annotation-list.component.html',
    styleUrl: './review-annotation-list.component.scss',
})
// Shared sidebar - one instance, always bound to the active slot's Asset (review.component.html binds
// [asset] and [slot] from whichever slot is active) - comments/annotations are active-slot-only, see
// docs/design/review-compare-assets.md §3.4.
export class ReviewAnnotationListComponent implements OnInit {
    @Input() asset: Asset;
    @Input() slot: Slot = Slot.Primary;

    public annotations$: Observable<Annotation[]>;
    public playbackState$: Observable<PlaybackState>;
    public form: FormGroup;
    public selected: number;

    private overlayRef?: OverlayRef;
    private mentionComponentRef?: MentionOverlayComponent;
    private _query: string;
    private _userMentions: User[];
    private readonly _mentionRegex = /@([a-zA-Z0-9_]*)$/;

    constructor(
        private annotationService: AnnotationService,
        private reviewVideoService: ReviewVideoService,
        private reviewService: ReviewService,
        private assetService: AssetService,
        private formBuilder: FormBuilder,
        private overlay: Overlay,
        private injector: Injector,
    ) {
        this.form = this.formBuilder.group({
            comment: ['', Validators.required],
            includeFrame: true,
        });
        this.selected = -1;
        this.mentionComponentRef = null;
        this._userMentions = [];
    }

    ngOnInit() {
        this.annotations$ = this.annotationService.comments$;
        this.playbackState$ = this.reviewVideoService.playbackState$
            .pipe(
                filter((playbackState) => playbackState.slot === this.slot),
            );
    }

    public addComment(frame: number, frameRate: number): void {
        const timestamp = frame / frameRate;
        const value = this.form.value;
        const annotation = new Annotation();
        annotation.comment = value.comment;
        if (this.asset.asset_type === AssetType.Video) {
            annotation.timestamp = value.includeFrame ? timestamp : -1;
        }
        else {
            annotation.timestamp = -1;
        }
        annotation.asset = this.asset.id;

        // Substitute user mentions
        for (const user of this._userMentions) {
            annotation.comment = annotation.comment.replace(user.name, user.id);
        }

        this.annotationService.create(annotation).subscribe(() => {
            this.reviewService.send({}, MessageType.AnnotationChange);
        });

        this.form.reset({
            comment: '',
            includeFrame: true,
        });
        this.form.markAsPristine();
        this.form.markAsUntouched();
    }

    select(index: number, annotation: Annotation) {
        this.selected = index;
        if (annotation.timestamp >= 0) {
            this.assetService.resolve([annotation.asset]).subscribe((assets) => {
                const videoAsset = deserializeAsset(assets[0]) as VideoAsset;
                const frame = videoAsset.timeToFrame(annotation.timestamp);
                this.reviewVideoService.setFrame(frame, Source.All, this.slot);

                const data = {
                    Frame: frame,
                };
                this.reviewService.sendActive(data);
            });
        }
    }

    onInput(event: any) {
        const textarea = event.target as HTMLTextAreaElement;
        const text = textarea.value;
        const cursor = textarea.selectionEnd;

        const match = this._mentionRegex.exec(text.slice(0, cursor));

        if (match) {
            this._query = match[1];
            this.openMentionPopup(textarea);
        }
        else {
            this.overlayRef?.dispose();
        }
    }

    private openMentionPopup(element: any) {
        if (this.mentionComponentRef !== null) {
            this.mentionComponentRef.filterUsers(this._query);
            return;
        }

        const verticalOffset = 28;
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(element)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: verticalOffset
                }
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: false,
            panelClass: 'mention-panel'
        });

        const injector = Injector.create({
            providers: [
                {provide: MENTION_QUERY, useValue: this._query}
            ],
            parent: this.injector
        })

        const portal = new ComponentPortal(MentionOverlayComponent, null, injector);
        const ref = this.overlayRef.attach(portal);
        this.mentionComponentRef = ref.instance;

        ref.instance.select.subscribe(user => {
            this._userMentions.push(user);
            this.insertAtCursor(element, `@${user.name}`, this._query.length + 1);
            this.closeOverlay();
        });
    }

    private closeOverlay() {
        this.overlayRef?.dispose();
        this.mentionComponentRef = null;
    }

    protected onKeydown(event: KeyboardEvent) {
        let triggered = false;
        if (event.code === 'Escape') {
            triggered = true;
            this.closeOverlay();
        }
        if (event.code === 'Enter') {
            triggered = true;
            this.mentionComponentRef?.triggerSelect();
        }
        if (event.key === 'ArrowUp') {
            triggered = true;
            this.mentionComponentRef?.selectOffset(-1);
        }
        if (event.key === 'ArrowDown') {
            triggered = true;
            this.mentionComponentRef?.selectOffset(1);
        }

        if (triggered) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    private insertAtCursor(textarea: HTMLTextAreaElement, text: string, offset: number) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const before = textarea.value.substring(0, start - offset);
        const after = textarea.value.substring(end);

        this.form.patchValue({
            comment: before + text + after
        });

        const newCursorPos = start + text.length;

        textarea.selectionStart = textarea.selectionEnd = newCursorPos;
    }
}
